import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiColumnType, ApiCustomField, ApiItemData } from '@agunity/api-v4';

import AgMultipleCustomField from '../../stories/Components/Cards/AgMultipleCustomField';

import { CustomFieldGroupProps } from '../../pages/OrganizationInfo/types';
import {
  CustomFieldColumnType,
  MANDATORY_LEVEL,
  MEMBER_PARENT_FIELD_TYPE_NUMBER,
} from '../../Constants';
import { useAppSelector } from '../../redux/hooks';

const CustomFields = ({
  targetId,
  details,
  tagList,
  handleChange,
  setHasCustomFieldErrors,
  showError,
}: {
    targetId?: number;
  details: ApiItemData[];
  tagList: ApiCustomField[];
  // eslint-disable-next-line no-unused-vars
  handleChange: (data: ApiItemData[]) => void;
    // eslint-disable-next-line no-unused-vars
    setHasCustomFieldErrors: (value: boolean) => void;
    showError: boolean;
}) => {
  const enumList = useAppSelector((state) => state.member.enumList || []);
  const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, boolean>>({});

  const customFieldGroupData: CustomFieldGroupProps[] = useMemo(() => {
    if (!tagList?.length) return [];
    let customFieldGroupData: CustomFieldGroupProps[] = [];

    // Custom Fields Headers
    const parentObjects = tagList
      .filter((obj) => obj.fieldType === MEMBER_PARENT_FIELD_TYPE_NUMBER)
      .filter((obj) => obj.targetId ? obj.targetId === targetId : true);
    parentObjects.forEach((parent) => {
      const groupName = parent.key ?? '';
      const parentId = parent.id;

      // Find all child objects with matching parentId
      const childObjects = tagList
        .filter((obj) => obj.parentId === parentId)
        .map((item) => {
          const fieldValue = ((details || []).find(dataItem => dataItem.key === item.key) || {}).value || '';
          const defaultItem = {
            name: item.key || '',
            value: fieldValue,
            unitId: item.unitId,
            mandatoryLevel: item.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
            fieldType: item.fieldType ?? CustomFieldColumnType.None,
            ...(item.maxLength ? { maxLength: item.maxLength } : {}),
          };

          if (
            [ApiColumnType.Enum, ApiColumnType.MultiEnum].includes(
              item.fieldType!
            )
          ) {
            const id = parseInt(defaultItem?.name || '');
            if (id) {
              const enu = enumList?.find((en) => en.id === id);
              const enuValues = enu?.values || [];
              let value = fieldValue;
              if (enuValues.length) {
                const values = fieldValue.split(',').map(Number);
                value = enuValues
                  .filter(({ id }) => values.includes(id!))
                  .map(({ name }) => name)
                  .join(',');
              }
              return {
                ...defaultItem,
                value,
              };
            }
          }
          return defaultItem;
        });

      // Push group object to customFieldGroupData array
      customFieldGroupData.push({
        groupName,
        data: childObjects,
      });
    });

    // Custom
    return customFieldGroupData || [];
  }, [details, enumList, tagList, targetId]);

  const handleCustomFieldErrors = useCallback((value: Record<string, boolean>) => {
    const validFields = Object.values(customFieldGroupData).flatMap(group => group.data);
    setCustomFieldErrors(prev => {
      const errors = { ...prev, ...value };
      return Object.fromEntries(
        Object.entries(errors).filter(([key]) =>
          validFields.some(field => field.name === key)
        )
      );
    });
  }, [customFieldGroupData]);

  useEffect(() => {
    setHasCustomFieldErrors(Object.values(customFieldErrors).some((error) => error));
  }, [customFieldErrors, setHasCustomFieldErrors]);

  return (
    <AgMultipleCustomField
      customFieldGroupData={customFieldGroupData}
      setDetails={handleChange}
      details={details}
      data={customFieldGroupData.map((customFieldData) => {
        return {
          title: customFieldData.groupName || '',
          editOnly: true,
          fields: customFieldData.data,
        };
      })}
      mainWidth={'auto'}
      enumList={enumList}
      setErrors={handleCustomFieldErrors}
      showError={showError}
    />
  );
};

export default CustomFields;
